import React from 'react'

import __uniqueId from 'lodash/uniqueId'
import styled from 'styled-components'

import { applyOpacity } from '@helpers/styled-helpers'

import { Typography } from '@components/ui/Typography'

const LabelText = styled(Typography)`
  color: ${({ theme }) => theme.colors.common.midnightEscape};
  line-height: 16px;
  letter-spacing: 0.26px;
  transition: color 250ms;
`

const LabelWrapper = styled.label`
  width: 100%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  gap: var(--gap);

  & > *:first-child {
    flex-shrink: 0;
    align-self: var(--align-control);
  }

  &[data-disabled='true'] {
    cursor: default;

    ${LabelText} {
      color: ${({ theme }) => applyOpacity(theme.colors.gray[100], 38)};
    }
  }
`

type ControlProps = {
  id?: string
  checked?: boolean
  disabled?: boolean
  name?: string
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  // When used with Switch or Checkbox, the onChange event is called with the isChecked parameter.
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, isChecked?: boolean) => void
  value?: string
}

const POSSIBLE_CONTROL_PROPS: (keyof Omit<ControlProps, 'disabled' | 'id'>)[] = [
  'checked',
  'name',
  'onBlur',
  'onChange',
  'value',
]

export interface FormControlLabelProps
  extends ControlProps,
    Omit<React.HTMLAttributes<HTMLLabelElement>, 'onBlur' | 'onChange'> {
  alignControl?: React.CSSProperties['alignSelf']
  className?: string
  control: React.ReactElement<ControlProps>
  htmlFor?: string
  gap?: number
  label: React.ReactNode
}

export const FormControlLabel = React.forwardRef<HTMLLabelElement, FormControlLabelProps>(
  (
    {
      alignControl = 'inherit',
      className,
      control,
      disabled = Boolean(control.props.disabled),
      htmlFor = __uniqueId('form-control-label-'),
      gap = 0,
      label,
      ...rest
    },
    ref
  ) => {
    // Separate control props from label props
    const { controlProps, labelProps } = React.useMemo(() => {
      const addedProps: Partial<ControlProps> = {
        disabled,
        id: htmlFor,
      }
      POSSIBLE_CONTROL_PROPS.forEach(key => {
        if (typeof control.props[key] === 'undefined' && typeof rest[key] !== 'undefined') {
          ;(addedProps as Record<keyof ControlProps, unknown>)[key] = rest[key]
        }
      })
      const controlPropKeys = Object.keys(addedProps)
      return {
        controlProps: addedProps,
        labelProps: Object.keys(rest)
          .filter(key => !controlPropKeys.includes(key))
          .reduce((acc, key) => {
            if (rest[key as keyof typeof rest] === undefined) {
              return acc
            } else {
              return {
                ...acc,
                [key as keyof typeof acc]: rest[key as keyof typeof rest],
              }
            }
          }, {} as React.HTMLAttributes<HTMLLabelElement>),
      }
    }, [control, disabled, htmlFor, rest])

    return (
      <LabelWrapper
        {...labelProps}
        ref={ref}
        htmlFor={htmlFor}
        className={className}
        data-disabled={disabled}
        style={{
          '--gap': `${gap}px`,
          '--align-control': alignControl,
        }}
      >
        {React.cloneElement(control, controlProps)}
        <LabelText size="400-xs" align="left" tag="span">
          {label}
        </LabelText>
      </LabelWrapper>
    )
  }
)

FormControlLabel.displayName = 'FormControlLabel'
